import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LoadEarlierBar from "./LoadEarlierBar";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";
import TableLoadingIndicator from "../TableLoadingIndicator";
import TableHeader from "./TableHeader";
import TableItem from "./TableItem";
import BookingActionsCell from "../../Modules/Bookings/Components/Table/Cells/BookingActionsCell";
import TimelineCurrentTimeMarker from "./TimelineCurrentTimeMarker";
import { DateTime } from "luxon";
import DateDivider from "./DateDivider";
import { discoverDateValue } from "../../Helpers/DateTimeHelper";
import InfiniteScrollZone from "./InfiniteScrollZone";

export const LOADING = {
    NORMAL: 'loading',
    AT_START: 'at_start',
    AT_END: 'at_end',
    REFRESH: 'refresh',
};

const TimelineTable = (props) => {
    const data = props.data ?? {};

    const {RowComponent, HeaderComponent, FilterRowComponent} = props;

    let [containerRef, setContainerRef] = useState(React.createRef());

    const [ nextRowDetails, setNextRowDetails ] = React.useState({
        row: null,
        index: null,
        isCurrentDay: true,
    });
    let [containerHeight, setContainerHeight] = useState(0);
    let [scrollPosition, setScrollPosition] = useState(0);

    // Rebuild array of refs for visit items each time the visits change
    // Used to keep track of dom elements for each visit when we want to move the timeline marker
    useEffect(() => {
        handleScroll();
        handleTimeTick();
    }, [data])

    const scrollToVisit = (index, pxOffset = -200) => {
        let $tableContainer = containerRef.current;
        let $row = document.getElementById(`row-${index}`);

        $tableContainer.scrollTo({
            top: $row.offsetTop + pxOffset,
            behavior: "smooth"
        })
    }

    const scrollToCurrentTime = () => {
        scrollToVisit(nextRowDetails.index, -220)
    }

    const handleTimeTick = () => {
        let [index, row, isSameDay] = findNextRow()

        if (!row) {
            return;
        }

        let rowDetails = {
            row: row,
            index: index,
            isCurrentDay: isSameDay
        };
        setNextRowDetails(rowDetails);
    }

    const findNextRow = (time) => {
        if (typeof(time) === 'undefined') {
            time = DateTime.local();
        }

        let checkRows = props.data;


        for (var index in checkRows) {
            let date = props.selectRowDate(checkRows[index]);
            let rowDate = DateTime.fromISO(date);
            if (rowDate > time) {
                return [index, checkRows[index], rowDate.hasSame(time, 'day')];
            }
        }
        return [null, null, false];
    }

    const handleScroll = () => {
        let $container = containerRef.current;
        if (!$container) {
            return;
        }
        setScrollPosition($container.scrollTop);
        setContainerHeight($container.scrollHeight - $container.offsetHeight);
    }

    const handleLoadEarlierClick = () => {
        let $tableContainer = containerRef.current;
        $tableContainer.scrollTo({
            top: 0,
            behavior: "smooth"
        })

        props.onLoadAtStart();
    }

    let previousVisitKeys = [];
    let rowsRendered = 0;

    return (<>
        <div className="timeline-table">
            <div style={{position: 'absolute', right: -38, top: -34, overflow: 'hidden'}}>
                <Fade in={props.loadingState === LOADING.REFRESH}>
                    <div></div>
                    {/*<CircularProgress size="1.5rem"/>*/}
                </Fade>
            </div>

            <div
                className="dataTableWrap sticky-table"
                onScroll={(e) => handleScroll(e)}
                ref={containerRef}
            >
                <table
                    id="timelineTable"
                    className="dataTable datatable-timeline table-nohover"
                >
                    <HeaderComponent
                        key="header"
                        columnData={props.columnData}
                    />

                    {(props.FilterRowComponent &&
                        <FilterRowComponent
                            key="filterrow"
                            columnData={props.columnData}
                        />
                    )}

                    {(props.hasMoreRecordsAtStart) && (
                        <LoadEarlierBar
                            key={'load-earlier'}
                            onClick={handleLoadEarlierClick}
                            isLoading={props.loadingState}
                        />
                    )}

                    {(data) && (
                        Object
                            .keys(data)
                            .map(key => {
                                previousVisitKeys.push(key);
                                let previousRow = (previousVisitKeys.length > 1)
                                    ? data[previousVisitKeys[previousVisitKeys.length-2]]
                                    : null;

                                return (<React.Fragment key={key}>
                                    <DateDivider
                                        key={`date-${props.selectRowDate(data[key]).substring(0, 10)}`}
                                        dateTime={props.selectRowDate(data[key])}
                                        previousDateTime={props.selectRowDate(previousRow)}
                                        hasFilterRow={!!props.FilterRowComponent}
                                    />

                                    <RowComponent
                                        key={key}
                                        index={key}
                                        rowNumber={rowsRendered++}
                                        row={data[key]}
                                        columnData={props.columnData}
                                        ActionsCellComponent={props.ActionsCellComponent}

                                        previousRow={(previousVisitKeys.length > 1)
                                            ? data[previousVisitKeys[previousVisitKeys.length-2]]
                                            : null
                                        }
                                        //previousVisitIndex={(previousVisitKeys.length > 1)
                                        //    ? previousVisitKeys[previousVisitKeys.length-2]
                                        //    : null
                                        //}

                                        onInformationRowToggle={() => {
                                            handleScroll();
                                            handleTimeTick();
                                        }}
                                    />
                                </React.Fragment>)
                            })
                    )}

                    { (Object.keys(data).length === 0 && !props.loadingState) && (
                        <tbody>
                            <tr>
                                <td colSpan="7" className="padding-1">
                                    <strong>No Records</strong>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>

                <TableLoadingIndicator
                    loadingState={props.loadingState}
                    reverseLoadingDirection={props.reverseLoadingDirection}
                />

                {(data && !(props.loadingState && !(props.loadingState === LOADING.AT_END))) &&
                    <InfiniteScrollZone
                        onLoad={props.onLoadAtEnd}
                        isLoading={(props.loadingState === LOADING.AT_END)}
                        hasMoreRecords={props.hasMoreRecordsAtEnd}
                        containerScrollTop={scrollPosition}
                        containerHeight={containerHeight}
                        visitsLoaded={Object.keys(data).length}
                        filters={props.filters}
                    />
                }
            </div>

            {(data && props.isTimeResponsive) && (
                <TimelineCurrentTimeMarker
                    nextRowDetails={nextRowDetails}
                    row={data}
                    filters={props.filters}
                    containerRef={containerRef}
                    onTick={handleTimeTick}
                    containerScrollTop={scrollPosition}
                    hasFilterRow={!!props.FilterRowComponent}
                />
            )}

        </div>

        <div className="margin-top-1">
            <div className="grid-x">
                <div className="cell small-6">

                    <div className="button-group">
                        <button
                            type="button"
                            className="button secondary"
                            onClick={scrollToCurrentTime}
                        >
                            <i className="fa fa-arrow-up"></i>&nbsp;
                            Now
                        </button>
                    </div>

                </div>
                <div className="cell small-6">

                    <div className="button-group align-right">

                        {(!!props.onExport) &&
                            <button
                                type="button"
                                className="button secondary"
                                onClick={(e) => props.onExport()}
                            >
                                Export
                            </button>
                        }

                    </div>

                </div>
            </div>
        </div>

    </>)
}

TimelineTable.propTypes = {
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    columnData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    filters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),

    loadingState: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    isTimeResponsive: PropTypes.bool,
    reverseLoadingDirection: PropTypes.bool,
    hasMoreRecordsAtStart: PropTypes.bool,
    hasMoreRecordsAtEnd: PropTypes.bool,
    onLoadAtStart: PropTypes.func,
    onLoadAtEnd: PropTypes.func,
    onExport: PropTypes.func,
}

TimelineTable.defaultProps = {
    HeaderComponent: TableHeader,
    FilterRowComponent: null,
    RowComponent: TableItem,

    ActionsCellComponent: BookingActionsCell, // TODO - generic version

    selectRowDate: (row) => discoverDateValue(row),

    isTimeResponsive: true,
    reverseLoadingDirection: false,
    hasMoreRecordsAtStart: false,
    hasMoreRecordsAtEnd: false,
    onLoadAtStart: () => null,
    onLoadAtEnd: () => null,
    onExport: null,
}

export default TimelineTable;
